import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import { Toaster } from './ui/toaster';

// Import all section renderers
import HeroSection from './sections/HeroSection';
import AboutSection from './sections/AboutSection';
import StatsSection from './sections/StatsSection';
import ExpoSection from './sections/ExpoSection';
import NewsSection from './sections/NewsSection';
import MeetupsSection from './sections/MeetupsSection';
import GallerySection from './sections/GallerySection';
import NewsletterSection from './sections/NewsletterSection';
import TextSection from './sections/TextSection';
import ImageTextSection from './sections/ImageTextSection';
import SlideshowSection from './sections/SlideshowSection';
import EmbedSection from './sections/EmbedSection';
import SponsorsSection from './sections/SponsorsSection';
import PressSection from './sections/PressSection';
import CTAStripSection from './sections/CTAStripSection';
import FeatureGridSection from './sections/FeatureGridSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DynamicHomepage = () => {
  const [sections, setSections] = useState([]);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetchHomepage();
  }, []);

  // Handle hash anchor scrolling after page loads, or scroll to top if no hash
  useEffect(() => {
    if (!loading && sections.length > 0) {
      if (location.hash) {
        // Small delay to ensure DOM is fully rendered
        const timeoutId = setTimeout(() => {
          const hash = location.hash.slice(1); // Remove the # character
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        
        return () => clearTimeout(timeoutId);
      } else {
        // No hash - scroll to top of the page
        window.scrollTo(0, 0);
      }
    }
  }, [loading, sections, location.hash]);

  // Update meta tags when homepage settings are loaded
  useEffect(() => {
    if (homepageSettings) {
      // Update document title
      if (homepageSettings.meta_title) {
        document.title = homepageSettings.meta_title;
      }
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      if (homepageSettings.meta_description) {
        metaDescription.setAttribute('content', homepageSettings.meta_description);
      }
      
      // Update or create og:image meta tag
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      // Always set the og:image from homepage settings (which includes fallback to default)
      ogImage.setAttribute('content', homepageSettings.og_image || '');
      
      // Also update twitter:image
      let twitterImage = document.querySelector('meta[property="twitter:image"]');
      if (!twitterImage) {
        twitterImage = document.createElement('meta');
        twitterImage.setAttribute('property', 'twitter:image');
        document.head.appendChild(twitterImage);
      }
      twitterImage.setAttribute('content', homepageSettings.og_image || '');
      
      // Update or create og:title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', homepageSettings.meta_title || 'Pulse NYC');
      
      // Update or create og:description
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      if (homepageSettings.meta_description) {
        ogDescription.setAttribute('content', homepageSettings.meta_description);
      }
    }
  }, [homepageSettings]);

  // Preload logo to prevent flash
  useEffect(() => {
    const preloadLogo = async () => {
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        const logoUrl = settingsRes.data?.logo_url;
        const logoUrlLight = settingsRes.data?.logo_url_light;
        
        // Add preload link for logo
        if (logoUrl) {
          let preloadLink = document.querySelector('link[rel="preload"][as="image"][data-logo="dark"]');
          if (!preloadLink) {
            preloadLink = document.createElement('link');
            preloadLink.rel = 'preload';
            preloadLink.as = 'image';
            preloadLink.href = logoUrl;
            preloadLink.setAttribute('data-logo', 'dark');
            document.head.appendChild(preloadLink);
          }
        }
        if (logoUrlLight) {
          let preloadLinkLight = document.querySelector('link[rel="preload"][as="image"][data-logo="light"]');
          if (!preloadLinkLight) {
            preloadLinkLight = document.createElement('link');
            preloadLinkLight.rel = 'preload';
            preloadLinkLight.as = 'image';
            preloadLinkLight.href = logoUrlLight;
            preloadLinkLight.setAttribute('data-logo', 'light');
            document.head.appendChild(preloadLinkLight);
          }
        }
      } catch (err) {
        // Silently fail - logo will still load normally
      }
    };
    preloadLogo();
  }, []);

  const fetchHomepage = async () => {
    try {
      const response = await axios.get(`${API}/homepage`);
      // API now returns { sections, meta_title, meta_description, og_image, menu_id }
      if (Array.isArray(response.data)) {
        // Backwards compatibility: if API returns array, use it directly
        setSections(response.data);
      } else {
        setSections(response.data.sections || []);
        setHomepageSettings({
          meta_title: response.data.meta_title,
          meta_description: response.data.meta_description,
          og_image: response.data.og_image,
          menu_id: response.data.menu_id
        });
      }
    } catch (err) {
      console.error('Failed to load homepage:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section) => {
    const anchor = section.anchor;
    
    const getSectionComponent = () => {
      switch (section.type) {
        case 'hero':
          return <HeroSection key={section.id} data={section.data} />;
        
        case 'news':
          return <NewsSection key={section.id} data={section.data} />;
        
        case 'feature_grid':
          // Check for legacy title-based mappings first for backwards compatibility
          if (section.title === 'About Section') {
            return <AboutSection key={section.id} data={section.data} />;
          } else if (section.title === 'Expo Highlights') {
            return <ExpoSection key={section.id} data={section.data} />;
          } else if (section.title === 'News Section') {
            return <NewsSection key={section.id} data={section.data} />;
          } else if (section.title === 'Meetups Section') {
            return <MeetupsSection key={section.id} data={section.data} />;
          } else if (section.title === 'Gallery Section') {
            return <GallerySection key={section.id} data={section.data} />;
          }
          // Default: use generic feature grid
          return <FeatureGridSection key={section.id} data={section.data} />;
        
        case 'cta_strip':
          if (section.title === 'Stats Section') {
            return <StatsSection key={section.id} data={section.data} />;
          }
          // Default: use generic CTA strip
          return <CTAStripSection key={section.id} data={section.data} />;
        
        case 'video_text':
          return <AboutSection key={section.id} data={{
            ...section.data,
            isVideoText: true
          }} />;
        
        case 'newsletter_flodesk':
          return <NewsletterSection key={section.id} data={section.data} />;
        
        case 'text':
          return <TextSection key={section.id} data={section.data} />;
        
        case 'image_text':
          return <ImageTextSection key={section.id} data={section.data} />;
        
        case 'slideshow':
          return <SlideshowSection key={section.id} data={section.data} />;
        
        case 'embed':
          return <EmbedSection key={section.id} data={section.data} />;
        
        case 'gallery':
          return <GallerySection key={section.id} data={section.data} />;
        
        case 'sponsors':
          return <SponsorsSection key={section.id} data={section.data} />;
        
        case 'press':
          return <PressSection key={section.id} data={section.data} />;
        
        default:
          // Log unknown section type for debugging
          console.warn(`Unknown section type: ${section.type}`, section);
          return null;
      }
    };

    const component = getSectionComponent();
    if (!component) return null;

    // Wrap with anchor div if anchor is set
    if (anchor) {
      return <div key={section.id} id={anchor}>{component}</div>;
    }
    return component;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-xl">Failed to load homepage</p>
          <p className="text-gray-600 mt-2">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar menuId={homepageSettings?.menu_id} />
      {sections.map(section => renderSection(section))}
      <Footer />
      <Toaster />
    </>
  );
};

export default DynamicHomepage;
